import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import FunnelLayout from "@/components/app/FunnelLayout";
import ExitIntentModal from "@/components/app/ExitIntentModal";
import QuizLink from "@/components/app/QuizLink";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const TABS = [
  {
    id: "outside",
    label: "🌟 איך זה נראה מבחוץ",
    sectionTitle: "ההצלחה החיצונית",
    rows: [
      { icon: "🏠", title: "דירה בבעלות", text: "עם משכנתה כמובן, אבל הבית רשום על שמכם בטאבו." },
      { icon: "🚗", title: "רכב חדש", text: "בליסינג או בהלוואת בלון, העיקר שהשנתון מתקדם." },
      { icon: "📸", title: "תמונות מחויכות", text: "אירועים, חוגים לילדים, ומראית עין של שליטה מלאה." },
    ],
  },
  {
    id: "inside",
    label: "🌪️ איך זה מרגיש מבפנים",
    sectionTitle: "החרדה הפנימית",
    rows: [
      { icon: "📱", title: "פחד מהסמס מהבנק", text: "חרדה משתקת בכל פעם שמתקבלת התראה על חריגה או חיוב." },
      { icon: "🔗", title: "משכנתה שנייה סמויה", text: 'אוסף הלוואות "קטנות" ופריסות אשראי שהופכות למפלצת חונקת.' },
      { icon: "🤫", title: "הסוואת המצב", text: "חיים בתחושה שאסור למעוד, מסתירים את המצב מהקרובים ואפילו מבני הזוג." },
    ],
  },
];

const PIE_COLORS = ["#d97706", "#ef4444", "#10b981", "#44403c"];
const PIE_DATA = [
  { name: "ריביות והחזרים", value: 38 },
  { name: "הוצאות מחיה", value: 32 },
  { name: "משכנתה", value: 22 },
  { name: "חיסכון", value: 8 },
];

const STATS = [
  { value: "₪8,000", title: "חיסכון חודשי ממוצע", text: 'מאתרים את הכסף שנשפך על החזרים כפולים וריביות נסתרות, ומחזירים אותו לתזרים שלכם בכל 1 לחודש.' },
  { value: "30", title: "ימים לשינוי מורגש", text: 'התהליך לא לוקח עשור. ארגון מחדש של התחייבויות מביא לתוצאות מהירות ומיידיות בחשבון העו"ש.' },
  { value: "0", title: "שעות עבודה נוספות", text: "הפתרון הוא לא לשחוק את עצמכם בעבודה שלישית, אלא לגרום לכסף ולנכסים שלכם לעבוד בשבילכם." },
];

export default function FunnelGuide1() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("outside");
  const [debtAmount, setDebtAmount] = useState(4500);

  const oxygen = useMemo(() => Math.round(debtAmount * 0.75), [debtAmount]);
  const barData = [
    { name: "היום", value: debtAmount, fill: "#ef4444" },
    { name: "אחרי איחוד", value: debtAmount - oxygen, fill: "#10b981" },
  ];

  const active = TABS.find((t) => t.id === tab);

  return (
    <FunnelLayout
      step={1}
      variant="dark"
      loadingMessages={["מנתח את הנתונים שלך...", "מאתר את החוסמים...", "מכין את המפה האישית שלך..."]}
    >
      {(lead) => (
        <>
          {/* HERO – personalized intro */}
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1.5 rounded-full bg-brand-amber/15 text-brand-amber font-bold text-sm border border-brand-amber/30 mb-6">
              מדריך 1: האמת שמאחורי הכסף
            </span>

            {lead?.first_name && (
              <motion.p
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="text-lg sm:text-xl text-stone-200 font-bold mb-5"
              >
                <span className="text-brand-amber">{lead.first_name}</span>, ברוך הבא לקהילת אמת או חובות! 👋
              </motion.p>
            )}

            <p className="text-base sm:text-lg text-stone-300 leading-[1.75] max-w-2xl mx-auto mb-8">
              אני <strong className="text-white">רז</strong>, וגם אני הייתי במקום שלך – עם חובות והלוואות שחונקות את החשבונות... עד שלמדתי איך המנגנון הפיננסי באמת עובד. ובשורות הבאות אראה לך איך משפחות שמרוויחות טוב מפסיקות לממן את הבנקים ומתחילות לחיות באמת.
            </p>

            <motion.h1
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-[28px] sm:text-4xl md:text-5xl lg:text-[56px] font-[800] leading-[1.15] md:leading-[1.1] tracking-tight break-words max-w-3xl mx-auto"
            >
              איך משפחות שמרוויחות טוב <span className="text-brand-amber">מפסיקות לממן את הבנקים</span> ומתחילות לחיות באמת.
            </motion.h1>

            <p className="mt-6 text-base sm:text-lg text-stone-300 leading-[1.75] max-w-2xl mx-auto">
              אתם קמים בבוקר, הולכים לעבודות תובעניות, לוקחים אחריות. על הנייר – מרוויחים מצוין. אבל בעשירי לחודש? הכסף מתאדה לתוך חור שחור של הלוואות וריביות. הגיע הזמן להבין למה זה קורה, ואיך משנים את זה.
            </p>
          </div>

          {/* Section 1: Trap intro + Dichotomy Tabs */}
          <div className="bg-white/5 backdrop-blur-md rounded-3xl border border-white/10 p-5 md:p-8 mb-12">
            <h2 className="text-2xl md:text-3xl font-[800] mb-3">המלכודת של "מעמד הביניים פלוס"</h2>
            <p className="text-stone-300 leading-[1.7] mb-6">
              תופעה שקטה שאף אחד לא מדבר עליה. הפער העצום בין איך שהחיים שלכם נראים כלפי חוץ, לבין החרדה הכלכלית שאתם חווים כלפי פנים. לחצו על הכרטיסיות כדי לראות את הפער.
            </p>

            <div className="flex p-1 bg-white/10 rounded-full mb-6" data-testid="dichotomy-tabs">
              {TABS.map((t) => {
                const isActive = tab === t.id;
                return (
                  <button
                    key={t.id}
                    data-testid={`tab-${t.id}`}
                    onClick={() => setTab(t.id)}
                    className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-3 rounded-full text-sm font-bold transition-all ${
                      isActive
                        ? "bg-brand-amber text-white shadow-lg"
                        : "text-stone-300 hover:text-white"
                    }`}
                  >
                    {t.label}
                  </button>
                );
              })}
            </div>

            <motion.div
              key={tab}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35 }}
            >
              <h3 className="text-lg font-[800] text-brand-amber mb-4 text-center">{active.sectionTitle}</h3>
              <div className="grid sm:grid-cols-3 gap-4">
                {active.rows.map((r) => (
                  <div key={r.title} className="bg-white/5 rounded-2xl p-5 border border-white/10">
                    <div className="text-3xl mb-2">{r.icon}</div>
                    <h4 className="font-[800] mb-1">{r.title}</h4>
                    <p className="text-sm text-stone-300 leading-relaxed">{r.text}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Section 2: Why the bank loves you */}
          <div className="bg-white/5 rounded-3xl border border-white/10 p-6 md:p-8 mb-12">
            <span className="inline-block text-xs font-bold tracking-widest uppercase text-brand-amber mb-3">נקודת המבט של המערכת</span>
            <h2 className="text-2xl md:text-3xl font-[800] mb-4">למה הבנק אוהב אתכם?</h2>
            <div className="space-y-4 text-stone-300 leading-[1.75] mb-6">
              <p>
                רוב האנשים בטוחים שאם הם במינוס או צריכים הלוואה, הם "גרועים בפיננסים". המערכת הבנקאית מטפחת את תחושת האשמה הזו.
              </p>
              <p>
                אבל בואו נסתכל על זה דרך המשקפיים של מנהל הסניף: אתם אנשים עובדים, עם משכורת גבוהה ודירה בבעלותכם. אתם תמיד דואגים לכסות חובות, גם אם זה אומר לקחת הלוואה חדשה כדי לסגור את הקודמת.
              </p>
              <p>
                מבחינת הבנק, אתם <strong className="text-white">תרנגולת שמטילה ביצי זהב</strong>. אתם משלמים אלפי שקלים בחודש רק על ריביות, וזה המודל העסקי שלהם.
              </p>
              <p className="text-sm text-stone-400">
                הגרף ממחיש לאן הולכת משכורת ממוצעת של "לקוח טוב" – נתח עצום נשאב בחזרה למערכת הבנקאית דרך החזרים וריביות, הרבה מעבר להוצאות המחיה בפועל.
              </p>
            </div>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={PIE_DATA}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {PIE_DATA.map((d) => (
                      <Cell key={d.name} fill={PIE_COLORS[PIE_DATA.indexOf(d)]} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#1c1917",
                      border: "1px solid rgba(255,255,255,0.2)",
                      borderRadius: 12,
                      color: "#fff",
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
              {PIE_DATA.map((d, i) => (
                <div key={d.name} className="flex items-center gap-2 text-sm">
                  <span className="w-3 h-3 rounded-full" style={{ background: PIE_COLORS[i] }} />
                  <span className="text-stone-300">{d.name}</span>
                  <span className="font-bold ms-auto">{d.value}%</span>
                </div>
              ))}
            </div>
          </div>

          {/* Section 3: Promise & stats */}
          <div className="bg-white/5 rounded-3xl border border-white/10 p-6 md:p-8 mb-12">
            <h2 className="text-2xl md:text-3xl font-[800] mb-3">ההבטחה של "אמת או חובות"</h2>
            <p className="text-stone-300 leading-[1.7] mb-6">
              השיטה שלנו לא דורשת לגזור כרטיסי אשראי או לספור כל שקל בסופר. אנחנו מתעסקים ב<strong className="text-brand-amber"> הנדסת התזרים</strong> שלכם מחדש. אלו התוצאות שמשפחות בקהילה משיגות:
            </p>
            <div className="grid sm:grid-cols-3 gap-4">
              {STATS.map((s) => (
                <div key={s.title} className="bg-white/5 rounded-2xl p-5 border border-white/10 text-center">
                  <div className="text-4xl md:text-5xl font-[800] text-brand-amber mb-2">{s.value}</div>
                  <h3 className="font-[800] mb-2">{s.title}</h3>
                  <p className="text-sm text-stone-300 leading-relaxed">{s.text}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Section 4: Monthly Oxygen Simulator */}
          <div className="bg-white text-brand-charcoal rounded-3xl p-6 md:p-8 mb-10 shadow-[0_24px_60px_rgba(0,0,0,0.2)]">
            <h2 className="text-2xl md:text-3xl font-[800] mb-3">ה"קסם" הפיננסי: איחוד הלוואות לבעלי נכסים</h2>
            <p className="text-stone-600 leading-[1.7] mb-6">
              אם יש לכם דירה, אתם מחזיקים בנשק שובר שוויון. "הלוואות קטנות" (רכב, מינוס, אשראי) נושאות ריביות מטורפות והחזרים חונקים. תהליך <strong className="text-brand-amber">איחוד הלוואות</strong> מכניס אותן תחת תנאי המשכנתה הזולים והארוכים. בדקו בעצמכם כיצד המהלך ישפיע עליכם:
            </p>

            <span className="inline-block px-3 py-1 rounded-full bg-amber-100 text-brand-amber font-bold text-xs mb-3">סימולטור חמצן חודשי</span>
            <h3 className="text-xl font-[800] mb-1">סך ההחזרים החודשיים שלכם כיום</h3>
            <p className="text-stone-500 mb-5 text-sm">(ללא משכנתה – הלוואות, תשלומים, כיסוי מינוס)</p>
            <div className="flex items-center gap-3 mb-6">
              <span className="text-2xl font-[800] text-brand-amber">₪</span>
              <Input
                type="number"
                value={debtAmount}
                onChange={(e) => setDebtAmount(Number(e.target.value) || 0)}
                data-testid="oxygen-amount-input"
                className="h-14 text-2xl font-[800] rounded-xl"
              />
            </div>
            <div className="bg-amber-50 rounded-2xl p-5 mb-5">
              <div className="text-sm text-stone-600 mb-1">החיסכון החודשי המשוער שלכם:</div>
              <div className="text-4xl font-[800] text-brand-amber" data-testid="oxygen-result">₪{oxygen.toLocaleString()}</div>
              <p className="text-xs text-stone-500 mt-2">סכום זה חוזר לתזרים שלכם בכל חודש לחופשות, חיסכון או שקט נפשי.</p>
            </div>
            <h4 className="font-[800] mb-3">השפעת איחוד ההלוואות על ההחזר החודשי</h4>
            <div className="h-48 min-h-[180px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={barData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" vertical={false} />
                  <XAxis dataKey="name" tick={{ fill: "#44403c" }} />
                  <YAxis tick={{ fill: "#44403c" }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#fff",
                      border: "1px solid #e7e5e4",
                      borderRadius: 12,
                    }}
                  />
                  <Bar dataKey="value" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <p className="mt-3 text-xs text-stone-500 leading-relaxed">
              * הסימולציה להמחשה בלבד ומבוססת על פריסה ממוצעת לטווח ארוך בריביות משכנתה לעומת ריביות של הלוואות צרכניות קצרות. הנתונים הסופיים כפופים לאישור הבנק.
            </p>
          </div>

          {/* Section 5: What's next */}
          <div className="bg-white/5 rounded-3xl border border-white/10 p-6 md:p-8 mb-10 text-center">
            <h2 className="text-2xl md:text-3xl font-[800] mb-3">מה הלאה? בדקו את עצמכם</h2>
            <p className="text-stone-300 leading-[1.7] max-w-2xl mx-auto">
              אם סך ההחזרים החודשיים שלכם עולה על <strong className="text-brand-amber">15% מההכנסה</strong>, ואתם ב"מינוס כרוני" למרות השכר הגבוה, זה הזמן לעצור. בנינו <QuizLink lead={lead} source="guide1_what_next">שאלון אבחון קצר ואנונימי</QuizLink> שיבדוק ב-2 דקות האם השיטה והדירה שלכם יכולים להוציא אתכם לחופש כלכלי.
            </p>
          </div>

          {/* Next */}
          <div className="flex flex-col items-center gap-3 py-6">
            <Button
              data-testid="funnel-next-btn"
              onClick={() => navigate(`/funnel/${lead.lead_id}/2`)}
              className="h-14 px-10 rounded-full bg-brand-amber hover:bg-brand-amberHover text-white font-bold pulse-cta"
            >
              למדריך הבא: מיפוי החוסמים
              <ArrowLeft className="w-5 h-5 me-2" />
            </Button>
            <span className="text-xs text-stone-400">לוקח פחות מ-2 דקות</span>
          </div>
          <ExitIntentModal lead={lead} source="guide1" />
        </>
      )}
    </FunnelLayout>
  );
}
